/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Dashboard widget types.
 *
 * Every widget is fed from one `POST /analytics/dashboards/{id}/run` response,
 * not from a request of its own. A twelve-tile dashboard otherwise opens twelve
 * connections at once, and on a tenant with a small pool that is the whole
 * pool.
 *
 * A widget whose report failed carries its own error and the rest still render.
 * One broken report should cost one tile, not the page.
 */

import PropTypes from 'prop-types';
import {
  Box, CircularProgress, Skeleton, Tooltip, Typography,
} from '@mui/material';
import {
  ArrowDownwardRounded, ArrowUpwardRounded, ErrorOutlineRounded,
  OpenInNewRounded,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import colors from 'styles/tokens/colors';
import ReportChart from '../ReportChart';
import ReportTable from '../ReportTable';
import { compact, formatValue } from '../chartPalette';
import { v2 as t, v2Tones, V2_DISPLAY_FONT_FAMILY } from 'styles/tokens/v2';

/* ------------------------------------------------------------ the shell */

/**
 * The frame every widget sits in: title, drill-through, and the states a tile
 * can be in. Kept here rather than repeated per widget so a loading tile is
 * the same size as the loaded one and the grid does not jump.
 */
export function WidgetFrame({ title, subtitle, onOpen, loading, error, children,
                             dragHandle }) {
  // v2 (design-reference "Sales CRM home"): a flat 10px card on the page
  // background, title in the card body with no rule under it.
  const isV2 = useTheme()?.pearlUi === 'v2';
  return (
    <Box
      sx={{
        height: '100%', display: 'flex', flexDirection: 'column',
        border: '1px solid', borderColor: isV2 ? t.border : 'divider',
        borderRadius: isV2 ? '10px' : '14px',
        backgroundColor: colors.background.white, overflow: 'hidden',
      }}
    >
      <Box
        className={dragHandle ? 'widget-drag-handle' : undefined}
        display="flex" alignItems="center" gap={1}
        sx={isV2 ? {
          px: 2, pt: 1.75, pb: 0.5, flexShrink: 0,
          cursor: dragHandle ? 'move' : 'default',
        } : {
          px: 1.75, py: 1.25, flexShrink: 0,
          cursor: dragHandle ? 'move' : 'default',
          borderBottom: '1px solid', borderColor: 'divider',
        }}
      >
        <Box flex={1} minWidth={0}>
          <Typography variant="body2" fontWeight={700} noWrap
                      sx={isV2 ? { fontSize: 15, fontWeight: 600, color: t.text } : undefined}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap display="block"
                        sx={isV2 ? { fontSize: 12, color: t.text2 } : undefined}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {onOpen && (
          <Tooltip title="Open the report behind this">
            <OpenInNewRounded
              onClick={(e) => { e.stopPropagation(); onOpen(); }}
              sx={{ fontSize: 16, cursor: 'pointer', color: isV2 ? t.text2 : colors.text.light,
                    '&:hover': { color: isV2 ? t.link : colors.brand.primary } }}
            />
          </Tooltip>
        )}
      </Box>

      <Box flex={1} minWidth={0} sx={{ p: isV2 ? 2 : 1.5, pt: isV2 ? 1 : 1.5, overflow: 'auto' }}>
        {loading ? (
          <Skeleton variant="rounded" height="100%" sx={{ minHeight: 80 }} />
        ) : error ? (
          <Box display="flex" flexDirection="column" alignItems="center"
               justifyContent="center" height="100%" gap={0.75} px={1}>
            <ErrorOutlineRounded sx={{ fontSize: 22, color: colors.status.warning.dot }} />
            <Typography variant="caption" color="text.secondary" textAlign="center">
              {error}
            </Typography>
          </Box>
        ) : children}
      </Box>
    </Box>
  );
}

WidgetFrame.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onOpen: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.string,
  children: PropTypes.node,
  dragHandle: PropTypes.bool,
};

/* --------------------------------------------------------------- KPI */

/**
 * A single number, optionally against a comparison period.
 *
 * The comparison is computed from a second run the backend does not do, so it
 * is only shown when the widget config asked for it and the data arrived.
 * Inventing a trend arrow from one number is worse than showing none.
 */
export function KpiWidget({ result, config, currency }) {
  const isV2 = useTheme()?.pearlUi === 'v2';
  const columns = result?.columns ?? [];
  const rows = result?.rows ?? [];

  // Prefer the aggregate the config names; otherwise the first aggregate; if
  // the report is not grouped at all, fall back to the row count, which is
  // what "how many open cases" means.
  const column = columns.find((c) => c.key === config?.value_key)
    ?? columns.find((c) => c.is_aggregate);

  let value;
  if (column) {
    value = rows.reduce((sum, row) => sum + (Number(row[column.key]) || 0), 0);
  } else {
    value = result?.row_count ?? 0;
  }

  const target = config?.target;
  const progress = target ? Math.min(100, Math.round((value / target) * 100)) : null;
  const previous = config?.previous_value;
  const delta = typeof previous === 'number' && previous !== 0
    ? Math.round(((value - previous) / Math.abs(previous)) * 100)
    : null;

  const display = column
    ? formatValue(value, column, currency)
    : value.toLocaleString();

  // Slightly smaller type for long amounts so A$2,248,085 still fits a KPI
  // tile without ellipsis or a M/K abbreviation that needs a tooltip to trust.
  const longAmount = display.length > 11;
  const label = config?.subtitle ?? column?.label ?? 'Records';

  if (isV2) {
    // v2 KPI tile (design-reference "Sales CRM home"): the frame title is the
    // label, then the number, then the comparison as a tinted chip.
    const tone = delta !== null && delta < 0 ? v2Tones.danger : v2Tones.success;
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" height="100%" px={0.5}>
        <Tooltip title={display} followCursor enterDelay={200} leaveDelay={0}>
          <Box component="span" sx={{ display: 'inline-block', maxWidth: '100%', width: 'fit-content' }}>
            <Typography
              sx={{
                mt: 0.5, fontFamily: V2_DISPLAY_FONT_FAMILY, color: t.text, fontWeight: 600, lineHeight: 1.15,
                letterSpacing: '-0.01em', fontSize: longAmount ? 24 : 30,
                fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere',
              }}
            >
              {display}
            </Typography>
          </Box>
        </Tooltip>
        <Typography noWrap sx={{ mt: 0.25, fontSize: 12, color: t.text2 }}>{label}</Typography>
        {delta !== null && (
          <Box sx={{ mt: 1, alignSelf: 'flex-start', px: 1, py: 0.25, borderRadius: '6px',
                     backgroundColor: tone.bg, color: tone.fg, fontSize: 12, fontWeight: 600 }}>
            {delta >= 0 ? '+' : '−'}{Math.abs(delta)}% vs previous
          </Box>
        )}
        {progress !== null && (
          <Box mt={1}>
            <Box sx={{ height: 6, borderRadius: 3, overflow: 'hidden', backgroundColor: t.hover }}>
              <Box sx={{ width: `${progress}%`, height: '100%', backgroundColor: t.primary }} />
            </Box>
            <Typography sx={{ mt: 0.5, fontSize: 12, color: t.text2 }}>
              {progress}% of {compact(target)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" height="100%" px={0.5}>
      <Tooltip title={display} followCursor enterDelay={200} leaveDelay={0}>
        {/* inline-block + fit-content: MUI anchors the tooltip to the child's
            box. A full-width noWrap Typography made that box the whole tile,
            so the bubble sat mid-card instead of near the cursor. */}
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            maxWidth: '100%',
            width: 'fit-content',
          }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            lineHeight={1.15}
            sx={{
              color: colors.text.dark,
              fontSize: longAmount ? '1.35rem' : undefined,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {display}
          </Typography>
        </Box>
      </Tooltip>

      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>

      {delta !== null && (
        <Box display="flex" alignItems="center" gap={0.25} mt={0.5}>
          {delta >= 0
            ? <ArrowUpwardRounded sx={{ fontSize: 14, color: colors.status.success.dot }} />
            : <ArrowDownwardRounded sx={{ fontSize: 14, color: colors.status.error.dot }} />}
          <Typography
            variant="caption" fontWeight={700}
            sx={{ color: delta >= 0 ? colors.status.success.text : colors.status.error.text }}
          >
            {Math.abs(delta)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            vs previous
          </Typography>
        </Box>
      )}

      {progress !== null && (
        <Box mt={1}>
          <Box sx={{ height: 5, borderRadius: 3, overflow: 'hidden',
                     backgroundColor: colors.background.offWhite }}>
            <Box sx={{ width: `${progress}%`, height: '100%',
                       backgroundColor: colors.brand.primary }} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {progress}% of {compact(target)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

KpiWidget.propTypes = {
  result: PropTypes.object,
  config: PropTypes.object,
  currency: PropTypes.string,
};

/* ------------------------------------------------------------- chart */

export function ChartWidget({ result, config, chartType, currency }) {
  return (
    <ReportChart result={result} config={config} chartType={chartType}
                 currency={currency} height={null} />
  );
}

ChartWidget.propTypes = {
  result: PropTypes.object,
  config: PropTypes.object,
  chartType: PropTypes.string,
  currency: PropTypes.string,
};

/* -------------------------------------------------------------- list */

export function ListWidget({ result, currency }) {
  return <ReportTable result={result} currency={currency} dense />;
}

ListWidget.propTypes = {
  result: PropTypes.object,
  currency: PropTypes.string,
};

/* -------------------------------------------------------------- text */

export function TextWidget({ config }) {
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {config?.text ?? ''}
    </Typography>
  );
}

TextWidget.propTypes = { config: PropTypes.object };

/* ---------------------------------------------------------- greeting */

/**
 * The time-of-day banner from the old home page, kept as a widget type.
 *
 * It was 60 lines of hardcoded gradients and emoji inside a 2,700-line
 * component. As a widget it is optional — someone who finds it twee can remove
 * it from their dashboard instead of being stuck with it.
 */
export function GreetingWidget({ config, userName }) {
  const isV2 = useTheme()?.pearlUi === 'v2';
  const hour = new Date().getHours();
  const period = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const name = userName ? `, ${userName}` : '';

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" height="100%">
      <Typography variant="h5" fontWeight={700}
                  sx={isV2 ? { fontSize: 22, fontWeight: 600, color: t.text } : undefined}>
        {period}{name}
      </Typography>
      <Typography variant="body2" color="text.secondary" mt={0.5}
                  sx={isV2 ? { fontSize: 14, color: t.text2 } : undefined}>
        {config?.message ?? new Date().toLocaleDateString(undefined, {
          weekday: 'long', day: 'numeric', month: 'long',
        })}
      </Typography>
    </Box>
  );
}

GreetingWidget.propTypes = { config: PropTypes.object, userName: PropTypes.string };

/* ------------------------------------------------------ spinner reuse */

export function WidgetLoading() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
      <CircularProgress size={20} />
    </Box>
  );
}
